<template>
  <modal-vue @close="handleToggleModal" :modalActive="modals[modalName]" :hasFooterBorder="false">
    <template #header>
      <div class="px-[16px] py-2">
        <ModalHeader
          :resourceType="translateResourceType(metadata?.resourceType) || 'Recurso'"
          :resourceId="metadata?.resourceId"
          :headerTitle="shareTitle"
          :breadcrumbs="visibleBreadcrumbs"
        />
      </div>
    </template>
    <v-row dense class="px-1 pt-2 pb-1">
      <v-col cols="6">
        <v-autocomplete
          v-model="moduleType"
          :items="moduleTypeOptions"
          item-title="label"
          item-value="value"
          :disabled="!flags.enabledDsAttendancePolicy"
          color="primary20"
          variant="outlined"
          density="compact"
          hide-details
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <SubjectsAutocomplete
          v-model="subjectsToShare"
          v-model:permission="permission"
          :items="subjects"
          :permission-options="permissionNewUsers"
          :disabled="isLoading || !canEditAccessLevel"
          :searching="isSearching"
          :has-more="permissionsStore.candidatePagination.hasMore"
          :loading-more="isLoadingResourceCandidates"
          :impact-warnings="impactWarnings"
          :get-subject-name="getSubjectName"
          :get-relation-label="getRelationLabel"
          @update:search="searchText = $event"
          @end-reached="onEndReached(true)"
        />
      </v-col>

      <Transition name="slide-down" mode="out-in">
        <v-col class="mt-3" v-if="isShareContext">
          <v-textarea
            v-model="messageNotify"
            class="message-to-share"
            color="primary20"
            label="Mensagem (Opcional)"
            placeholder="Escreva uma mensagem para notificar os usuários..."
            :disabled="!isNotifyShare"
          />
          <v-checkbox
            v-model="isNotifyShare"
            color="primary"
            fieldId="isNotify"
            label="Notificar Pessoas"
          />
        </v-col>
        <v-col v-else cols="12" class="mt-2">
          <div class="text-left">
            <PermissionsAccordionList
              :permission-groups="permissionGroups"
              :permission-users="permissionUsers"
              v-model:filter-text="filterText"
              v-model:show-filter-input="showFilterInput"
              :is-loading="isLoading"
              :is-loading-filter="isLoadingFilter"
              :has-more="permissionsStore.sharedPermissionsPagination.hasMore"
              :loading-more="permissionsStore.isLoadingShared"
              :show-inheritance="showInheritance"
              :can-edit-access-level="canEditAccessLevel"
              :is-marked-for-removal="isMarkedForRemoval"
              :get-relation-label="getRelationLabel"
              :get-selectable-permissions="getSelectablePermissions"
              :get-sorted-permissions-for-display="getSortedPermissionsForDisplay"
              :resolve-resource-name="resolveResourceName"
              :get-pending-relation="getPendingRelation"
              @clear-filter="clearFilter"
              @toggle-inheritance="toggleInheritance"
              @track-change="({ subject, val }) => trackChange(subject, val)"
              @toggle-removal="toggleRemoval"
              @end-reached="onEndReachedSharedPermissions(true)"
            />
          </div>
        </v-col>
      </Transition>
    </v-row>

    <template #footer>
      <Transition name="fade" mode="out-in">
        <v-row>
          <v-col cols="12" md="6">
            <outline-button @click="handleToggleModal" width="100%" text="Cancelar" />
          </v-col>
          <v-col cols="12" md="6">
            <filled-button
              @click="handleSubmit"
              :text="isShareContext ? 'Enviar' : 'Concluido'"
              height="52px"
              width="100%"
              :loading="isLoading"
              :disabled="isLoading || isUnauthorize || !canEditAccessLevel"
            />
          </v-col>
        </v-row>
      </Transition>
    </template>
  </modal-vue>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import { EModals, useModalStore, type shareModalDataType } from '@/pinia-store/useModalStore'
import { computed, onUpdated, ref, watch } from 'vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import {
  AuthorizationRelationResponseExpanded,
  permissionPowerMap,
  resourceHierarchyMap
} from '@/Network/Types/Responses/AuthorizationResponses'
import {
  AuthorizationResourcesAndSubjects,
  AuthorizationRelations,
  AuthorizationPermissions,
  PermissionImpactResponse
} from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToast } from 'vue-toastification'
import { DEFAULT_MESSAGES } from '@/hooks/useCommon'
import { mdiAccount, mdiAccountMultiple } from '@mdi/js'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'
import Network from '@/Network'
import { UpsertPermissionsRequest } from '@/Network/Types/Requests/AuthorizationRequests'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import ModalHeader from './commons/ModalHeader.vue'
import SubjectsAutocomplete from '@/components/designSystem/Inputs/SubjectsAutocomplete.vue'
import PermissionsAccordionList from './commons/PermissionsAccordionList.vue'
import { ScheduleManagementStrategy } from './strategies/ScheduleManagementStrategy'
import { DsAttendanceStrategy } from './strategies/DsAttendanceStrategy'
import { StationExceptionalitiesStrategy } from './strategies/StationExceptionalitiesStrategy'
import { NpsStrategy } from './strategies/NpsStrategy'
import { PostStationStrategy } from './strategies/PostStationStrategy'
import { DocumentationStrategy } from './strategies/DocumentationStrategy'
import { moduleTypeOptionsFor } from './strategies/moduleTypeOptions'
import type { ModuleTypeStrategy } from './strategies/ModuleTypeStrategy'

const { flags } = useFeatureFlags()

const modalName = EModals.SHARE
const { modals } = storeToRefs(useModalStore())
const modalStore = useModalStore()
const handleToggleModal = () => {
  clearFields()
  modalStore.toggleModal(modalName)
}
const toast = useToast()
const permissionsStore = usePermissionsStore()
const { resourceCandidates, isLoadingResourceCandidates, isLoadingShared } =
  storeToRefs(permissionsStore)

const isUnauthorize = ref(false)

const permissionsData = ref<AuthorizationRelationResponseExpanded[]>([])
const originPermissionsDataMap = new Map<number, AuthorizationRelations>()

const pendingChangesMap = ref(new Map<number, AuthorizationRelationResponseExpanded>())

const impactWarnings = ref<PermissionImpactResponse[]>([])
const isCheckingImpact = ref(false)

const REMOVE_ACCESS_VALUE = 'Remover Acesso'

const MODULE_TYPE_STRATEGIES: Record<string, ModuleTypeStrategy> = {
  schedule_management: new ScheduleManagementStrategy(),
  ds_attendance: new DsAttendanceStrategy(),
  station_exceptionalities: new StationExceptionalitiesStrategy(),
  nps: new NpsStrategy(),
  post_station: new PostStationStrategy(),
  professional_documents: new DocumentationStrategy()
}

const moduleTypeOptions = computed(() =>
  moduleTypeOptionsFor(metadata.value?.resourceType as AuthorizationResourcesAndSubjects)
)
const moduleType = ref('schedule_management')

const activeStrategy = computed((): ModuleTypeStrategy | null => {
  return MODULE_TYPE_STRATEGIES[moduleType.value] ?? null
})

const effectiveResourceType = computed((): AuthorizationResourcesAndSubjects => {
  const base = metadata.value?.resourceType as AuthorizationResourcesAndSubjects
  const strategy = activeStrategy.value
  if (!strategy) return base
  return strategy.resolveResourceType(base)
})

const searchText = ref('')
const isSearching = ref(false)
const isLoading = ref(false)
const isLoadingFilter = ref(false)

const showFilterInput = ref(false)
const filterText = ref('')

const showInheritance = ref<Record<number, boolean>>({})

const canEditAccessLevel = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.AccessLevelEditing,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})

const permissionGroups = computed(() => {
  return permissionsData.value.filter(
    (permission) => permission.subject_type === AuthorizationResourcesAndSubjects.Group
  )
})

const permissionUsers = computed(() => {
  return permissionsData.value.filter(
    (permission) => permission.subject_type === AuthorizationResourcesAndSubjects.User
  )
})

const subjects = computed(() => {
  return resourceCandidates.value.map((candidate) => ({
    title: candidate.name,
    subtitle: candidate.email || candidate.description || '',
    value: {
      id: String(candidate.id),
      type: candidate.type
    },
    icon:
      candidate.type === AuthorizationResourcesAndSubjects.User ? mdiAccount : mdiAccountMultiple,
    raw: candidate
  }))
})

// === MUDANÇA PRINCIPAL AQUI ===
// A lista de baixo (permissões existentes) some se:
// 1. Tiver itens selecionados para compartilhar (subjectsToShare > 0)
// 2. OU se o usuário estiver digitando na busca principal (searchText > 0)
const isShareContext = computed(() => {
  return subjectsToShare.value.length > 0 || (searchText.value && searchText.value.length > 0)
})

function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

const onEndReached = (isIntersecting: boolean) => {
  if (
    isIntersecting &&
    permissionsStore.candidatePagination.hasMore &&
    !isLoadingResourceCandidates.value
  ) {
    if (!metadata.value) return
    permissionsStore.fetchResourceCandidates(
      effectiveResourceType.value,
      Number(metadata.value.resourceId),
      Number(metadata.value.companyId),
      searchText.value,
      true
    )
  }
}

const onEndReachedSharedPermissions = (isIntersecting: boolean) => {
  if (
    isIntersecting &&
    permissionsStore.sharedPermissionsPagination.hasMore &&
    !isLoadingShared.value
  ) {
    fetchPermissions(filterText.value, true)
  }
}

watch(
  searchText,
  debounce(async (term: string) => {
    if (!modals.value[modalName] || !metadata.value) return

    await permissionsStore.fetchResourceCandidates(
      effectiveResourceType.value,
      Number(metadata.value.resourceId),
      Number(metadata.value.companyId),
      term,
      false
    )
  }, 300)
)

watch(
  filterText,
  debounce(async (val: string) => {
    if (!modals.value[modalName] || isShareContext.value) return

    isLoadingFilter.value = true
    try {
      await fetchPermissions(val, false)
    } finally {
      isLoadingFilter.value = false
    }
  }, 400)
)

const fetchPermissions = async (search?: string, isLoadMore = false) => {
  try {
    if (!metadata.value) return

    const companyId = Number(metadata.value.companyId)

    const permissions = await permissionsStore.fetchSharedPermissions(
      effectiveResourceType.value,
      metadata.value.resourceId,
      companyId,
      search,
      isLoadMore
    )

    const mappedPermissions = permissions.map((p) => {
      if (pendingChangesMap.value.has(p.id)) {
        return { ...p, resource_relation: pendingChangesMap.value.get(p.id)!.resource_relation }
      }
      return p
    }) as AuthorizationRelationResponseExpanded[]

    if (isLoadMore) {
      permissionsData.value.push(...mappedPermissions)
    } else {
      permissionsData.value = mappedPermissions
      if (!search) {
        originPermissionsDataMap.clear()
      }
    }

    mappedPermissions.forEach((permission) => {
      if (!originPermissionsDataMap.has(permission.subject_id)) {
        originPermissionsDataMap.set(permission.subject_id, permission.resource_relation)
      }
    })
  } catch (error) {
    // `getApiErrorStatus` desembrulha o ServiceError do PermissionService; o
    // `axios.isAxiosError` antigo deixava de casar e o 403 caía no vazio.
    if (getApiErrorStatus(error) === 403) {
      isUnauthorize.value = true
    } else {
      // Antes: qualquer outra falha era engolida e a lista simplesmente não aparecia.
      toast.error(
        getApiErrorMessage(error, 'Não foi possível carregar as permissões compartilhadas.')
      )
    }
  }
}

const getPendingRelation = (subjectId: number): string | null => {
  return (pendingChangesMap.value.get(subjectId)?.resource_relation as string | undefined) ?? null
}

const getEffectiveRelation = (subject: AuthorizationRelationResponseExpanded): string => {
  return (getPendingRelation(subject.subject_id) ?? subject.resource_relation) as string
}

const trackChange = (subject: AuthorizationRelationResponseExpanded, newValue: any) => {
  const object = pendingChangesMap.value.set(subject.subject_id, {
    ...subject,
    resource_relation: newValue
  })

  debouncedCheckImpact()

  return object
}

const fetchInitialCandidates = async () => {
  if (!metadata.value) return

  await permissionsStore.fetchResourceCandidates(
    effectiveResourceType.value,
    Number(metadata.value.resourceId),
    Number(metadata.value.companyId)
  )
}

onUpdated(async () => {
  if (!metadata.value || isLoading.value) return
})

watch(
  () => modals.value[modalName],
  async (isOpen) => {
    if (isOpen && metadata.value) {
      try {
        isLoading.value = true
        await Promise.all([fetchPermissions(), fetchInitialCandidates()])
      } finally {
        isLoading.value = false
      }
    } else {
      clearFields()
    }
  }
)

const permission = ref<AuthorizationRelations>(AuthorizationRelations.BasicEditor)

const COMMON_PERMISSIONS = {
  parent: { label: 'Proprietário', value: AuthorizationRelations.Parent, disabled: true },
  basicReader: { label: 'Leitor Básico', value: AuthorizationRelations.BasicReader },
  intermediateReader: {
    label: 'Leitor Intermediário',
    value: AuthorizationRelations.IntermediateReader
  },
  advancedReader: { label: 'Leitor Avançado', value: AuthorizationRelations.AdvancedReader },
  basicEditor: { label: 'Editor Básico', value: AuthorizationRelations.BasicEditor },
  intermediateEditor: {
    label: 'Editor Intermediário',
    value: AuthorizationRelations.IntermediateEditor
  },
  contentAdmin: { label: 'Administrador de Conteúdo', value: AuthorizationRelations.ContentAdmin },
  generalAdmin: { label: 'Administrador Geral', value: AuthorizationRelations.GeneralAdmin },
  emitter: { label: 'Emissor', value: AuthorizationRelations.Emitter },
  basicReceiver: { label: 'Receptor Básico', value: AuthorizationRelations.BasicReceiver },
  advancedReceiver: { label: 'Receptor Avançado', value: AuthorizationRelations.AdvancedReceiver },
  extractor: { label: 'Extrator', value: AuthorizationRelations.Extractor },
  qualityAdmin: { label: 'Configurador', value: AuthorizationRelations.QualityAdmin },
  technicalEvaluator: {
    label: 'Avaliador Técnico',
    value: AuthorizationRelations.TechnicalEvaluator
  },
  institutionalEvaluator: {
    label: 'Avaliador Institucional',
    value: AuthorizationRelations.InstitutionalEvaluator
  },
  analyticsViewer: {
    label: 'Analista de Resultados',
    value: AuthorizationRelations.AnalyticsViewer
  },
  operator: { label: 'Operação', value: AuthorizationRelations.Operator },
  medicalCoordinator: {
    label: 'Tático',
    value: AuthorizationRelations.MedicalCoordinator
  },
  auditAnalyst: {
    label: 'Análise de Risco',
    value: AuthorizationRelations.AuditAnalyst
  },
  technicalValidator: {
    label: 'Técnica',
    value: AuthorizationRelations.TechnicalValidator
  },
  strategicValidator: {
    label: 'Estratégica',
    value: AuthorizationRelations.StrategicValidator
  },
  executiveValidator: {
    label: 'Executiva',
    value: AuthorizationRelations.ExecutiveValidator
  },
  documentationAnalyst: {
    label: 'Analista de Documentação',
    value: AuthorizationRelations.DocumentationAnalyst
  },
  financialAnalyst: {
    label: 'Analista Financeiro',
    value: AuthorizationRelations.FinancialAnalyst
  }
}

const permissionNewUsers = computed(() => {
  const strategy = activeStrategy.value
  if (strategy) return strategy.getPermissionsForNewUsers()
  return [
    COMMON_PERMISSIONS.basicReader,
    COMMON_PERMISSIONS.intermediateReader,
    COMMON_PERMISSIONS.advancedReader,
    COMMON_PERMISSIONS.basicEditor,
    COMMON_PERMISSIONS.intermediateEditor,
    COMMON_PERMISSIONS.contentAdmin,
    COMMON_PERMISSIONS.generalAdmin
  ]
})

const subjectsToShare = ref<{ id: string; type: AuthorizationResourcesAndSubjects }[]>([])
const isNotifyShare = ref<boolean>(true)
const messageNotify = ref<string>('')

const handleAddPermissions = async (
  resourceId: number,
  resourceType: AuthorizationResourcesAndSubjects,
  companyId: number
) => {
  const payload: UpsertPermissionsRequest = {
    resource_id: +resourceId,
    message: messageNotify.value,
    notify_share: isNotifyShare.value,
    subjects: subjectsToShare.value.map((subject) => {
      return {
        resource_relation: permission.value,
        subject_id: Number(subject.id),
        subject_type: subject.type
      }
    })
  }

  const strategy = activeStrategy.value
  if (strategy) {
    await strategy.addPermissions(resourceId, companyId, payload, effectiveResourceType.value)
    return
  }

  await Network.permission.addPermissions(resourceId, resourceType, payload, companyId)
}

const handleUpdatePermissions = async (
  resourceId: number,
  resourceType: AuthorizationResourcesAndSubjects,
  companyId: number
) => {
  const subjectsToUpdate = Array.from(pendingChangesMap.value.values()).flatMap((permission) => {
    if (originPermissionsDataMap.get(permission.subject_id) === permission.resource_relation)
      return []

    const relationValue = permission.resource_relation as string
    const isDelete = relationValue === REMOVE_ACCESS_VALUE

    const originalRelation = originPermissionsDataMap.get(permission.subject_id)

    if (!isDelete && originalRelation === permission.resource_relation) return []

    let relationToSend = permission.resource_relation
    if (isDelete) {
      relationToSend = originalRelation || AuthorizationRelations.BasicReader
    }

    return [
      {
        id: permission.subject_id,
        resource_relation: relationToSend,
        subject_id: Number(permission.subject_id),
        subject_type: permission.subject_type,
        isDelete
      }
    ]
  })

  if (subjectsToUpdate.length === 0) return

  const payload: UpsertPermissionsRequest = {
    resource_id: resourceId,
    subjects: subjectsToUpdate
  }

  const strategy = activeStrategy.value
  if (strategy) {
    await strategy.upsertPermissions(resourceId, companyId, payload, effectiveResourceType.value)
    return
  }

  await Network.permission.upsertPermissions(resourceId, resourceType, payload, companyId)
}

const handleSubmit = async () => {
  if (!metadata.value) return
  try {
    isLoading.value = true

    const rType = effectiveResourceType.value
    const companyId = Number(metadata.value.companyId)

    if (isShareContext.value) {
      await handleAddPermissions(metadata.value.resourceId, rType, companyId)
      toast.success('Permissões adicionadas com sucesso')
    } else {
      await handleUpdatePermissions(metadata.value.resourceId, rType, companyId)
      toast.success('Permissões atualizadas com sucesso')
    }

    handleToggleModal()
  } catch (err) {
    // Antes: `err.response.data.message` extraído na mão — com o ServiceError do
    // PermissionService isso viraria `undefined` e a mensagem do backend sumiria.
    toast.error(getApiErrorMessage(err, DEFAULT_MESSAGES.error))
  } finally {
    isLoading.value = false
  }
}

const permissionsForSelect = computed(() => {
  const strategy = activeStrategy.value
  if (strategy) return strategy.getPermissionsForSelect()
  return [
    COMMON_PERMISSIONS.parent,
    COMMON_PERMISSIONS.basicReader,
    COMMON_PERMISSIONS.intermediateReader,
    COMMON_PERMISSIONS.advancedReader,
    COMMON_PERMISSIONS.basicEditor,
    COMMON_PERMISSIONS.intermediateEditor,
    COMMON_PERMISSIONS.contentAdmin,
    COMMON_PERMISSIONS.generalAdmin
  ]
})

const metadata = computed((): shareModalDataType | undefined => {
  return modalStore.metadata as shareModalDataType | undefined
})

const shareTitle = computed(() => {
  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 0) {
    return `Compartilhar "${metadata.value.breadcrumb[metadata.value.breadcrumb.length - 1]}"`
  }
  return `Compartilhar "${metadata.value?.resourceName || ''}"`
})

const visibleBreadcrumbs = computed(() => {
  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 1) {
    return metadata.value.breadcrumb
  }
  return []
})

const clearFilter = () => {
  showFilterInput.value = false
  filterText.value = ''
  fetchPermissions()
}

function clearFields() {
  subjectsToShare.value = []
  permissionsData.value = []
  messageNotify.value = ''
  isUnauthorize.value = false
  searchText.value = ''
  impactWarnings.value = []
  isCheckingImpact.value = false
  pendingChangesMap.value.clear()
  originPermissionsDataMap.clear()
  permissionsStore.clearResourceCandidates()
  permissionsStore.resetSharedPagination()
  clearFilter()
  showInheritance.value = {}
  moduleType.value = 'schedule_management'
}

const isMarkedForRemoval = (id: number) => {
  return (pendingChangesMap.value.get(id)?.resource_relation as any) === REMOVE_ACCESS_VALUE
}

const toggleRemoval = (subject: AuthorizationRelationResponseExpanded) => {
  if (isMarkedForRemoval(subject.subject_id)) {
    const pendingObj = pendingChangesMap.value.get(subject.subject_id) as any
    const restoreValue =
      pendingObj?._previous_relation || originPermissionsDataMap.get(subject.subject_id)

    if (restoreValue) {
      trackChange(subject, restoreValue)
    }

    if (restoreValue === originPermissionsDataMap.get(subject.subject_id)) {
      pendingChangesMap.value.delete(subject.subject_id)
    }
  } else {
    const currentRelation = getEffectiveRelation(subject)

    pendingChangesMap.value.set(subject.subject_id, {
      ...subject,
      resource_relation: REMOVE_ACCESS_VALUE as any,
      _previous_relation: currentRelation
    } as any)
  }

  debouncedCheckImpact()
}

const ALL_RELATION_LABELS = Object.fromEntries(
  Object.values(COMMON_PERMISSIONS).map((p) => [p.value, p.label])
)

const getRelationLabel = (relation: string) => {
  const found = permissionsForSelect.value.find((p) => p.value === relation)
  return found?.label ?? ALL_RELATION_LABELS[relation] ?? relation
}

const getSubjectName = (id: number, type: string) => {
  const existing = permissionsData.value.find((p) => p.subject_id === id && p.subject_type === type)
  if (existing) return existing.subject_name

  const candidate = subjects.value.find((s) => Number(s.value.id) === id && s.value.type === type)
  if (candidate) return candidate.title

  return 'Usuário/Grupo'
}

const handleCheckImpact = async () => {
  if (!metadata.value) return

  const newSubjects = subjectsToShare.value.map((s) => ({
    subject_id: Number(s.id),
    subject_type: s.type
  }))

  const pendingSubjects = Array.from(pendingChangesMap.value.values()).map((p) => ({
    subject_id: Number(p.subject_id),
    subject_type: p.subject_type
  }))

  const allSubjectsMap = new Map()
  ;[...newSubjects, ...pendingSubjects].forEach((s) => {
    const key = `${s.subject_type}:${s.subject_id}`
    allSubjectsMap.set(key, s)
  })

  const finalSubjects = Array.from(allSubjectsMap.values())

  if (finalSubjects.length === 0) {
    impactWarnings.value = []
    return
  }

  isCheckingImpact.value = true
  try {
    impactWarnings.value = await permissionsStore.checkPermissionImpact({
      resource_id: metadata.value.resourceId,
      resource_type: effectiveResourceType.value,
      subjects: finalSubjects
    })
  } finally {
    isCheckingImpact.value = false
  }
}

const debouncedCheckImpact = debounce(handleCheckImpact, 500)

watch(moduleType, () => {
  const strategy = activeStrategy.value
  permission.value = strategy ? strategy.getDefaultPermission() : AuthorizationRelations.BasicEditor

  if (modals.value[modalName] && metadata.value) {
    isLoading.value = true

    subjectsToShare.value = []
    searchText.value = ''
    permissionsStore.clearResourceCandidates()

    Promise.all([fetchPermissions(), fetchInitialCandidates()]).finally(() => {
      isLoading.value = false
    })
  }
})

watch(
  subjectsToShare,
  () => {
    debouncedCheckImpact()
  },
  { deep: true }
)

const toggleInheritance = (id: number, forceState?: boolean) => {
  if (forceState !== undefined) {
    showInheritance.value = { ...showInheritance.value, [id]: forceState }
  } else {
    showInheritance.value = { ...showInheritance.value, [id]: !showInheritance.value[id] }
  }
}

const translateResourceType = (type?: string | AuthorizationResourcesAndSubjects) => {
  if (!type) return ''
  const map: Record<string, string> = {
    [AuthorizationResourcesAndSubjects.Company]: 'Empresa',
    [AuthorizationResourcesAndSubjects.HealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.Schedule]: 'Escala',
    [AuthorizationResourcesAndSubjects.DsAttendanceCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.DsAttendance]: 'Escala',
    [AuthorizationResourcesAndSubjects.NpsCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.NpsHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.PostStationCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.PostStationHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.ProfessionalDocumentsCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule]: 'Escala'
  }
  return map[type as string] || type
}

const getHighestInheritedPower = (subject: AuthorizationRelationResponseExpanded): number => {
  if (!subject.inheritances || subject.inheritances.length === 0) return 0
  let maxPower = 0
  subject.inheritances.forEach((inh) => {
    const power = permissionPowerMap[inh.resource_relation as AuthorizationRelations] || 0
    if (power > maxPower) maxPower = power
  })
  return maxPower
}

const getSelectablePermissions = (subject: AuthorizationRelationResponseExpanded) => {
  const minPowerRequired = getHighestInheritedPower(subject)
  return permissionsForSelect.value.map((p) => ({
    ...p,
    disabled:
      !!('disabled' in p && p.disabled) ||
      (permissionPowerMap[p.value as AuthorizationRelations] ?? 0) <= minPowerRequired
  }))
}

const resolveResourceName = (permItem: any) => {
  if (permItem.resource_name) return permItem.resource_name

  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 0) {
    const itemLevel = resourceHierarchyMap[permItem.resource_type]
    if (itemLevel && itemLevel > 0 && metadata.value.breadcrumb.length >= itemLevel) {
      return metadata.value.breadcrumb[itemLevel - 1]
    }
  }

  return `ID: ${permItem.resource_id}`
}

const getSortedPermissionsForDisplay = (subject: AuthorizationRelationResponseExpanded) => {
  const currentTypeId = metadata.value?.resourceId
  const currentType = effectiveResourceType.value

  const items = (subject.inheritances || []).map((inh) => {
    const isMatch =
      inh.resource_type === currentType && Number(inh.resource_id) === Number(currentTypeId)

    return {
      ...inh,
      resource_name: isMatch ? metadata.value?.resourceName : inh.resource_name,
      is_current_context: isMatch,
      is_inherited: true
    }
  })

  if (subject.resource_relation) {
    items.push({
      resource_type: subject.resource_type,
      resource_id: subject.resource_id,
      resource_relation: subject.resource_relation,
      resource_name: metadata.value?.resourceName,
      is_current_context: true,
      is_inherited: false
    })
  }

  return items.sort((a, b) => {
    const levelA = resourceHierarchyMap[a.resource_type] || 99
    const levelB = resourceHierarchyMap[b.resource_type] || 99
    return levelA - levelB
  })
}
</script>

<style scoped>
.fade-enter-active {
  transition: opacity 0.25s;
}
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter {
  opacity: 0;
}

.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.message-to-share :deep(.v-field__input) {
  height: 220px;
}

.pointer-events-none-children :deep(.v-select) {
  pointer-events: none;
}

.permission-select-dense :deep(.v-field__input) {
  min-height: 32px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  font-size: 13px !important;
}
.permission-select-dense :deep(.v-field__append-inner) {
  padding-top: 4px !important;
}
</style>
